"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useRouter } from "next/navigation"
import { account } from "@/lib/appwrite"
import { logActivity } from "@/lib/activity-log"
import { LoginBranding } from "@/components/login/login-branding"
import { LoginCard } from "@/components/login/login-card"
import { errText } from "@/lib/errors"

// ============================================
// CEO REQUIREMENT: Staff ID Login
// Login using payroll ID numbers (4-6 digits)
// No email/password - just enter your ID number
//
// Single-session enforcement (auto-takeover):
// - Only one device per staff member at a time
// - Logging in quietly signs out any other/ghost session and proceeds —
//   no "already logged in on another device" prompt (it fired on nearly
//   every login because abandoned sessions linger for ~1 year)
// ============================================

export default function LoginPage() {
  const router = useRouter()
  const [staffId, setStaffId] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState("")
  const [checkingSession, setCheckingSession] = useState(true)
  const submittingRef = useRef(false)

  // Check if user already has an active session
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 5000) // 5s timeout — don't block login
        const res = await fetch("/api/auth/profile", {
          credentials: "include",
          signal: controller.signal,
        })
        clearTimeout(timeout)
        if (res.ok) {
          // Only auto-redirect if BOTH user AND profile resolved.
          // If profile is null (transient DB hiccup, missing row), stay on /login —
          // otherwise /landing's `getCurrentProfile()` redirect bounces us back here
          // and we get an infinite loop.
          const data = await res.json().catch(() => null)
          if (data && data.profile) {
            router.push("/landing")
            return
          }
        }
      } catch {
        // No session or timeout — proceed to login
      } finally {
        setCheckingSession(false)
      }
    }
    checkExistingSession()
  }, [router])

  const handleNumberPress = useCallback(
    (num: string) => {
      if (staffId.length < 6) {
        setStaffId((prev) => prev + num)
        setError("")
      }
    },
    [staffId.length]
  )

  const handleBackspace = useCallback(() => {
    setStaffId((prev) => prev.slice(0, -1))
    setError("")
  }, [])

  const performLogin = useCallback(async () => {
    if (staffId.length < 4) {
      setError("Please enter your 4-6 digit ID number")
      return
    }

    if (submittingRef.current) return
    submittingRef.current = true

    setLoading(true)
    setLoadingMessage("Signing in...")
    setError("")

    try {
      const response = await fetch("/api/auth/staff-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ staffId }),
        credentials: "include",
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Login failed")
      }

      setLoadingMessage("Authenticating...")

      try {
        await account.deleteSession("current")
      } catch {
        // No existing session - continue
      }

      await account.createSession(data.userId, data.secret)

      // Mint a JWT from the just-established client session and hand THAT to
      // the server. The server validates the JWT against Appwrite and derives
      // the userId from the validated session — never trusting body-supplied
      // identity. Closes a same-origin privilege-escalation hole where any
      // authenticated caller could mint a session cookie for any other user.
      const { jwt: clientJwt } = await account.createJWT()

      const sessionRes = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jwt: clientJwt }),
        credentials: "include",
      })
      if (!sessionRes.ok) {
        // Without this route's cookies the next navigation bounces straight
        // back to the PIN pad — surface the real reason (429 wait time, 503,
        // …) instead of flashing a false "Welcome" and silently looping
        // (ship-audit 2026-08-19 P1-1).
        const sessionErr = (await sessionRes.json().catch(() => null)) as { error?: string } | null
        throw new Error(sessionErr?.error || "Couldn't finish signing in. Please try again.")
      }

      // Show personalized welcome with staff member's name
      const firstName = data.profile?.full_name?.split(' ')[0] || 'back'
      setLoadingMessage(`Welcome, ${firstName}!`)

      logActivity({
        staffId: data.userId,
        staffName: data.profile?.full_name || staffId,
        staffRole: data.profile?.role || "unknown",
        action: "login",
        category: "auth",
        description: `${data.profile?.full_name || staffId} logged in (${data.profile?.role || "unknown"})`,
        route: "/login",
      })

      router.push(data.redirectTo)
    } catch (err: unknown) {
      const message =
        errText(err, "Login failed. Please try again.")
      setError(message)
      setLoadingMessage("")
    } finally {
      setLoading(false)
      submittingRef.current = false
    }
  }, [staffId, router])

  const handleLogin = useCallback(() => performLogin(), [performLogin])

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && staffId.length >= 4 && !loading) {
        handleLogin()
      } else if (e.key === "Backspace") {
        handleBackspace()
      } else if (e.key === "Escape") {
        setStaffId("")
      } else if (/^[0-9]$/.test(e.key)) {
        handleNumberPress(e.key)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [staffId, loading, handleLogin, handleBackspace, handleNumberPress])

  // Loading state while checking session
  if (checkingSession) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-linear-to-br from-navy-700 via-navy-800 to-navy-900">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
          <span className="text-white/80 text-lg">
            Checking authentication...
          </span>
        </div>
      </div>
    )
  }

  return (
    <main id="main-content" role="main">
      <div className="min-h-dvh flex flex-row">
        {/* Left Half - Branding */}
        <LoginBranding />

        {/* Right Half - Login Card */}
        <LoginCard
          staffId={staffId}
          error={error}
          loading={loading}
          loadingMessage={loadingMessage}
          onNumberPress={handleNumberPress}
          onBackspace={handleBackspace}
          onLogin={handleLogin}
        />
      </div>
    </main>
  )
}
